import { Alert, Snackbar } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CancelFlightDialog from "../../../../components/element/CancelFlightDialog";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import VerificationIcon from "../../../../components/element/VerificationIcon";
import { useFormData } from "../../../../helpers/FormDataProvider";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { useConfiguration } from "../../../../services/ConfigurationProvider";
import { StorageContext } from "../../../../services/StorageProvider";
import {
  getDiplomatsPassenger,
  getGovernmentPassenger,
  getInfantPassenger,
  getMilitaryPassenger,
} from "../../../../utils/configUtils";
import {
  AirportType,
  ClearanceStatus,
  DisputeStatus,
  FLIGHT_PLAN_STATUS,
  HISTORICAL_DATA_ALERT_MESSAGES,
} from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import {
  getAirportData,
  getCommercialFlightPlanId,
  getHistoricalFlightPlanData,
  normalizeCommercialFlightPlan,
  setHistoricalFlightPlanData,
} from "../../../../utils/utils";
import AddPassengerCountDialog from "../../Flight Roster/Edit Flight Roster/Views/AddPassengerCountDialog";
import DisputeReasonDialog from "../../Flight Roster/Edit Flight Roster/Views/DisputeReasonDialog";
import PassengerCountDisplay from "../../Flight Roster/Edit Flight Roster/Views/PassengerCountDisplay";
import PassengerCountMismatchDialog from "../../Flight Roster/Edit Flight Roster/Views/PassengerCountMismatchDialog";
import ViewHistoricalDataTable from "./Tables/ViewHistoricalDataTable";
import "./ViewHistoricalData.css";
import ViewHistoricalDataApprovedView from "./Views/ViewHistoricalDataApprovedView";
import ViewHistoricalDataHeader from "./Views/ViewHistoricalDataHeader";
import ViewHistoricalDataPaymentSummary from "./Views/ViewHistoricalDataPaymentSummary";

const ViewHistoricalData = () => {
  const { userData } = useFormData();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { airportData } = useContext(StorageContext);

  const {
    fetchCommercialFlightPlanApprovals,
    updateAirlinePassengerCount,
    editCommercialFlightPlan,
    fetchCommercialFlightPlan,
    approveAACommercialFlightPlan,
    approveAirlineCommercialFlightPlan,
    checkAirlineApprovalDispute,
    fetchCommercialEscort,
    cancelCommercialFlightPlan,
  } = useAirlineAdminApi(userData.isAAUser);
  const [flightPlanDetail, setFlightPlanDetail] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddPassengerCountDialog, setShowAddPassengerCountDialog] =
    useState(false);
  const [passengerCountData, setPassengerCountData] = useState(null);
  const [editedFlightDetails, setEditedFlightDetails] = useState({});
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showDisputeReasonDialog, setShowDisputeReasonDialog] = useState(false);
  const [
    showPassengerCountMismatchDialog,
    setShowPassengerCountMismatchDialog,
  ] = useState(false);
  const [disputeData, setDisputeData] = useState(null);
  const [showDisputeSuccessMessage, setShowDisputeSuccessMessage] =
    useState(false);
  const [showCancelFlightDialog, setShowCancelFlightDialog] = useState(false);
  const { airlineData } = useContext(StorageContext);
  const { configurationData } = useConfiguration();
  const flightPlanId = getCommercialFlightPlanId(flightPlanDetail);

  const governmentPassengerEnabled = getGovernmentPassenger(configurationData);
  const diplomatsPassengerEnabled = getDiplomatsPassenger(configurationData);
  const militaryPassengerEnabled = getMilitaryPassenger(configurationData);
  const infantPassengerEnabled = getInfantPassenger(configurationData);

  const {
    data: flightPlanApprovals,
    isLoading: isLoadingFlightPlanApprovals,
    isFetching: isFetchingFlightPlanApprovals,
  } = useQuery({
    queryKey: ["get-commercial-flight-plan-approvals", flightPlanId],
    queryFn: () => fetchCommercialFlightPlanApprovals(flightPlanId),
    enabled: !!flightPlanId,
  });

  // Query to fetch flight plan data
  const { data: flightPlanData, refetch: refetchFlightPlan } = useQuery({
    queryKey: ["get-commercial-flight-plan", flightPlanId],
    queryFn: () => fetchCommercialFlightPlan(flightPlanId),
    enabled: !!flightPlanId,
  });

  // Handle the query data when it's available - this ensures deadheadCrews is preserved
  useEffect(() => {
    if (flightPlanData) {
      // Handle both response structures: { statusCode, statusMsg, data } or direct object
      const flightPlanResponse = normalizeCommercialFlightPlan(
        flightPlanData?.data || flightPlanData
      );

      if (flightPlanResponse) {
        const dataToStore = {
          ...flightPlanResponse,
          ...(flightPlanResponse.deadheadCrews !== undefined
            ? { deadheadCrews: flightPlanResponse.deadheadCrews }
            : {}),
        };

        const storedFlightPlan = setHistoricalFlightPlanData(dataToStore);
        setFlightPlanDetail(storedFlightPlan);
        updateFlightPlanData(storedFlightPlan);

        // Update passenger count data if available
        if (
          dataToStore?.airlineExemptedPassengerCount > 0 ||
          dataToStore?.airlinePassengerCount > 0
        ) {
          // Calculate total exempted based on configuration flags
          let calculatedTotalExempted = 0;

          if (infantPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineInfant || 0;
          }

          if (militaryPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineMilitary || 0;
          }

          if (governmentPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineGovernment || 0;
          }

          if (diplomatsPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineDiplomats || 0;
          }

          const airlineData = {
            totalBoarded: dataToStore?.airlinePassengerCount,
            totalExempted: calculatedTotalExempted,
            infant: dataToStore?.airlineInfant,
            military: dataToStore?.airlineMilitary,
            government: dataToStore?.airlineGovernment,
            diplomat: dataToStore?.airlineDiplomats,
          };
          setPassengerCountData(airlineData);
        }
      }
    }
  }, [flightPlanData, infantPassengerEnabled, militaryPassengerEnabled, governmentPassengerEnabled, diplomatsPassengerEnabled]);

  const getCommercialFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) => fetchCommercialFlightPlan(planId),
    onSuccess: (response) => {
      if (response?.data) {
        const normalizedResponse = normalizeCommercialFlightPlan(response.data);
        const dataToStore = {
          ...normalizedResponse,
          deadheadCrews: normalizedResponse.deadheadCrews || [],
        };
        const storedFlightPlan = setHistoricalFlightPlanData(dataToStore);
        setFlightPlanDetail(storedFlightPlan);
        updateFlightPlanData(storedFlightPlan);

        // Update passenger count data if available
        if (
          dataToStore?.airlineExemptedPassengerCount > 0 ||
          dataToStore?.airlinePassengerCount > 0
        ) {
          // Calculate total exempted based on configuration flags
          let calculatedTotalExempted = 0;

          if (infantPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineInfant || 0;
          }

          if (militaryPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineMilitary || 0;
          }

          if (governmentPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineGovernment || 0;
          }

          if (diplomatsPassengerEnabled) {
            calculatedTotalExempted += dataToStore?.airlineDiplomats || 0;
          }

          const airlineData = {
            totalBoarded: dataToStore?.airlinePassengerCount,
            totalExempted: calculatedTotalExempted,
            infant: dataToStore?.airlineInfant,
            military: dataToStore?.airlineMilitary,
            government: dataToStore?.airlineGovernment,
            diplomat: dataToStore?.airlineDiplomats,
          };
          setPassengerCountData(airlineData);
        }
      }
    },
  });

  const updatePassengerCountMutation = useMutation({
    mutationFn: ({ planId, disputeData }) =>
      updateAirlinePassengerCount(planId, disputeData),
    onSuccess: (response) => {
      setShowAddPassengerCountDialog(false);

      // Use the same approach as EditFlightRoster - fetch the updated flight plan
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanId,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanId,
      ]);
    },
  });

  const editCommercialFlightPlanMutation = useMutation({
    mutationFn: ({ planId, data }) => editCommercialFlightPlan(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanId,
      ]);

      // Use the same approach as EditFlightRoster - fetch the updated flight plan
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanId,
      });
      setIsEditing(false);
    },
  });

  const approveFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) =>
      userData.isAAUser
        ? approveAACommercialFlightPlan(planId)
        : approveAirlineCommercialFlightPlan(planId),

    onSuccess: () => {
      setShowDisputeSuccessMessage(true);
      getCommercialFlightPlanMutation.mutate({
        planId: flightPlanId,
      });
      queryClient.invalidateQueries([
        "get-commercial-flight-plan-approvals",
        flightPlanId,
      ]);
    },
  });

  const checkDisputeMutation = useMutation({
    mutationFn: ({ planId, raiseDispute, disputeReason }) =>
      checkAirlineApprovalDispute(planId, raiseDispute, disputeReason),
    onSuccess: (response) => {
      setShowDisputeReasonDialog(false);
      if (response?.data?.isDispute === true) {
        setDisputeData(response?.data);
        // If disputeStatus is "RAISED", it means a reason was submitted, so close the mismatch dialog
        if (response?.data?.disputeStatus === DisputeStatus.RAISED) {
          setShowPassengerCountMismatchDialog(false);
          // Refresh data after dispute with reason is submitted
          getCommercialFlightPlanMutation.mutate({
            planId: flightPlanId,
          });
          queryClient.invalidateQueries([
            "get-commercial-flight-plan-approvals",
            flightPlanId,
          ]);
        } else {
          // Regular dispute without reason, show mismatch dialog
          setShowPassengerCountMismatchDialog(true);
        }
      } else {
        // Refresh the flight plan data after successful confirmation
        setShowDisputeSuccessMessage(true);

        getCommercialFlightPlanMutation.mutate({
          planId: flightPlanId,
        });
        queryClient.invalidateQueries([
          "get-commercial-flight-plan-approvals",
          flightPlanId,
        ]);
      }
    },
  });

  const cancelFlightPlanMutation = useMutation({
    mutationFn: ({ planId }) => cancelCommercialFlightPlan(planId),
    onSuccess: () => {
      setShowCancelFlightDialog(false);
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER);
    },
  });

  const updateFlightPlanData = (planData) => {
    // Preserve any unsaved changes when updating flight plan data
    setEditedFlightDetails((prevEditedDetails) => ({
      aircraftRegistrationNumber:
        prevEditedDetails?.aircraftRegistrationNumber ||
        planData.aircraftRegistrationNumber,
      aircraftTypeId:
        prevEditedDetails?.aircraftTypeId || planData.aircraftTypeId || "",
      departureDelay:
        prevEditedDetails?.departureDelay ||
        (planData.departureDelay ?? "00:00:00"),
      arrivalDelay:
        prevEditedDetails?.arrivalDelay ||
        (planData.arrivalDelay ?? "00:00:00"),
      departureDelayFromArrivalAirport:
        prevEditedDetails?.departureDelayFromArrivalAirport ||
        (planData.departureDelayFromArrivalAirport ?? "00:00:00"),
      passengerBoardingCount: planData.passengerBoardingCount,
      passengerScreeningCount: planData.scannedPassengerCount,
    }));
  };

  useEffect(() => {
    const parsedData = getHistoricalFlightPlanData();
    if (parsedData) {
      const storedFlightPlanId = getCommercialFlightPlanId(parsedData);

      setFlightPlanDetail(parsedData);
      updateFlightPlanData(parsedData);

      // Initialize passenger count data if available
      if (
        parsedData?.airlineExemptedPassengerCount > 0 ||
        parsedData?.airlinePassengerCount > 0
      ) {
        // Calculate total exempted based on configuration flags
        let calculatedTotalExempted = 0;

        if (infantPassengerEnabled) {
          calculatedTotalExempted += parsedData?.airlineInfant || 0;
        }

        if (militaryPassengerEnabled) {
          calculatedTotalExempted += parsedData?.airlineMilitary || 0;
        }

        if (governmentPassengerEnabled) {
          calculatedTotalExempted += parsedData?.airlineGovernment || 0;
        }

        if (diplomatsPassengerEnabled) {
          calculatedTotalExempted += parsedData?.airlineDiplomats || 0;
        }

        const airlineData = {
          totalBoarded: parsedData?.airlinePassengerCount,
          totalExempted: calculatedTotalExempted,
          infant: parsedData?.airlineInfant,
          military: parsedData?.airlineMilitary,
          government: parsedData?.airlineGovernment,
          diplomat: parsedData?.airlineDiplomats,
          deadheadCrews: parsedData?.deadheadCrews,
        };
        setPassengerCountData(airlineData);
      }

      if (!storedFlightPlanId) {
        setAlertMessage(
          "Unable to load this flight plan. Please reopen it from Historical Data."
        );
        setShowAlert(true);
      }
    }
  }, []);

  const handleAddPassengerCountSave = (data) => {
    if (!flightPlanId) {
      setAlertMessage(
        "Unable to update passengers because this flight plan could not be loaded."
      );
      setShowAlert(true);
      return;
    }

    const addPassengerCountData = {
      airlinePassengerCount: data?.totalBoarded,
      airlineInfant: data?.infant ?? 0,
      airlineMilitary: data?.military ?? 0,
      airlineGovernment: data?.government ?? 0,
      airlineDiplomats: data?.diplomat ?? 0,
    };

    // Always include airlineDeadheadCrewIds, even if empty
    if (
      data?.airlineDeadheadCrewIds &&
      Array.isArray(data.airlineDeadheadCrewIds)
    ) {
      addPassengerCountData.airlineDeadheadCrewIds =
        data.airlineDeadheadCrewIds;
    } else {
      addPassengerCountData.airlineDeadheadCrewIds = [];
    }

    // Call update API
    updatePassengerCountMutation.mutate({
      planId: flightPlanId,
      disputeData: addPassengerCountData,
    });
    setPassengerCountData(data);
  };

  const editFlightPlanWithValidation = () => {
    if (!flightPlanId) {
      setAlertMessage(
        "Unable to save this flight plan because its details are not fully loaded yet."
      );
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftTypeId ||
      editedFlightDetails.aircraftTypeId === ""
    ) {
      setAlertMessage("Please select aircraft type.");
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftRegistrationNumber ||
      editedFlightDetails?.aircraftRegistrationNumber === ""
    ) {
      setAlertMessage("Please enter the tail number.");
      setShowAlert(true);
    } else {
      editCommercialFlightPlanMutation.mutate({
        planId: flightPlanId,
        data: editedFlightDetails,
      });
    }
  };

  const handleSaveFlight = () => {
    editFlightPlanWithValidation();
  };
  const { data: commercialEscortService } = useQuery({
    queryKey: ["get-commercial-escort", flightPlanId],
    queryFn: () => fetchCommercialEscort(flightPlanId),
    enabled:
      !!flightPlanId &&
      userData.isAAUser !== null &&
      userData.isAAUser !== undefined,
  });
  const escortArrivalStatus =
    commercialEscortService?.data?.escortService?.arrivalStatus ===
      ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.arrivalStatus;
  const escortDepartureStatus =
    commercialEscortService?.data?.escortService?.departureStatus ===
      ClearanceStatus.NOT_REQUIRED
      ? null
      : commercialEscortService?.data?.escortService?.departureStatus;

  const handleConfirmFlight = () => {
    if (!flightPlanId) {
      setAlertMessage(
        "Unable to confirm this flight plan because its details are not fully loaded yet."
      );
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftTypeId ||
      editedFlightDetails.aircraftTypeId === ""
    ) {
      setAlertMessage("Please select aircraft type.");
      setShowAlert(true);
    } else if (
      !editedFlightDetails?.aircraftRegistrationNumber ||
      editedFlightDetails?.aircraftRegistrationNumber === ""
    ) {
      setAlertMessage("Please enter the tail number.");
      setShowAlert(true);
    } else {
      const isArrivalApproved =
        escortArrivalStatus === ClearanceStatus.PAID ||
        escortArrivalStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        escortArrivalStatus === ClearanceStatus.CLEARANCE_REJECTED ||
        escortArrivalStatus === ClearanceStatus.CORRECTION_DONE;

      const isDepartureApproved =
        escortDepartureStatus === ClearanceStatus.PAID ||
        escortDepartureStatus === ClearanceStatus.CLEARANCE_GRANTED ||
        escortDepartureStatus === ClearanceStatus.CLEARANCE_REJECTED ||
        escortDepartureStatus === ClearanceStatus.CORRECTION_DONE;

      if (flightPlanDetail?.requiresEscortService === false) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanId });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanId,
            raiseDispute: false,
          });
        }
      } else if (
        escortArrivalStatus === null &&
        escortDepartureStatus === null
      ) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanId });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanId,
            raiseDispute: false,
          });
        }
      } else if (escortArrivalStatus === null) {
        if (isDepartureApproved) {
          if (userData.isAAUser) {
            approveFlightPlanMutation.mutate({ planId: flightPlanId });
          } else {
            checkDisputeMutation.mutate({
              planId: flightPlanId,
              raiseDispute: false,
            });
          }
        } else {
          setAlertMessage(
            "Special Onboarding approval must be completed before approving the flight plan."
          );
          setShowAlert(true);
        }
      } else if (escortDepartureStatus === null) {
        if (isArrivalApproved) {
          if (userData.isAAUser) {
            approveFlightPlanMutation.mutate({ planId: flightPlanId });
          } else {
            checkDisputeMutation.mutate({
              planId: flightPlanId,
              raiseDispute: false,
            });
          }
        } else {
          setAlertMessage(
            "Special Onboarding approval must be completed before approving the flight plan."
          );
          setShowAlert(true);
        }
      } else if (isArrivalApproved && isDepartureApproved) {
        if (userData.isAAUser) {
          approveFlightPlanMutation.mutate({ planId: flightPlanId });
        } else {
          checkDisputeMutation.mutate({
            planId: flightPlanId,
            raiseDispute: false,
          });
        }
      } else {
        setAlertMessage(
          "Special Onboarding must be approved before confirming the flight plan."
        );
        setShowAlert(true);
      }
    }
  };

  // Function to handle alert close
  const handleAlertClose = () => {
    setShowAlert(false);
  };

  // Function to handle dispute success message close
  const handleDisputeSuccessClose = () => {
    setShowDisputeSuccessMessage(false);
  };

  // Function to handle dispute reason submission
  const handleDisputeReasonSave = (disputeReason) => {
    if (!flightPlanId) {
      setAlertMessage(
        "Unable to submit a dispute because this flight plan could not be loaded."
      );
      setShowAlert(true);
      return;
    }

    const mutationData = {
      planId: flightPlanId,
      raiseDispute: true,
    };

    // Only add disputeReason if it's not empty
    if (disputeReason && disputeReason.trim()) {
      mutationData.disputeReason = disputeReason.trim();
    }

    checkDisputeMutation.mutate(mutationData);
  };

  const handleRejectBMSCount = () => {
    // Show the dispute reason dialog
    setShowPassengerCountMismatchDialog(false);
    setShowDisputeReasonDialog(true);
  };

  const handleUpdateAirlineCount = () => {
    // Show the add passenger count dialog
    setShowPassengerCountMismatchDialog(false);
    setShowAddPassengerCountDialog(true);
  };

  const handleCancelFlight = () => {
    if (!flightPlanId) {
      setShowCancelFlightDialog(false);
      setAlertMessage(
        HISTORICAL_DATA_ALERT_MESSAGES.CANCEL_FLIGHT_DETAILS_NOT_LOADED
      );
      setShowAlert(true);
      return;
    }

    cancelFlightPlanMutation.mutate({
      planId: flightPlanId,
    });
  };
  const handlePrint = () => {
    window.print();
  };

  const getPassengerMismatchData = () => {
    const bmsCount = disputeData?.scannedPassengerCount || 0;
    const exempted = disputeData?.exemptedPassengerCount || 0;
    const billableCount = bmsCount - exempted;

    return {
      bmsCount,
      exempted,
      billableCount,
    };
  };
  const getAirlineCountClassName = () => {
    if (flightPlanDetail?.disputeStatus === DisputeStatus.AA_FORCED_BMS_COUNT) {
      return "view-historical-passenger-count-rejected";
    }
    if (
      flightPlanDetail?.disputeStatus ===
      DisputeStatus.AA_ACCEPTED_AIRLINE_COUNT &&
      userData.isAAUser
    ) {
      return "view-historical-passenger-count-accepted";
    }
    if (flightPlanDetail?.disputeStatus === DisputeStatus.RAISED) {
      return "view-historical-passenger-count-rejected";
    }
    return "";
  };
  const departureAirportType = getAirportData(
    airportData,
    flightPlanDetail?.departureAirportId
  )?.airportType;
  const isDepartureAirportInternational =
    departureAirportType === AirportType.INTERNATIONAL;

  return (
    <div className="edit-flight-roster-container">
      {updatePassengerCountMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          {updatePassengerCountMutation.data?.data ||
            "Passenger count updated successfully"}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {updatePassengerCountMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {updatePassengerCountMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {editCommercialFlightPlanMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          Flight plan updated successfully
        </SnackbarAlert>
      ) : (
        ""
      )}
      {editCommercialFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {editCommercialFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {approveFlightPlanMutation.isSuccess ? (
        <SnackbarAlert type={"success"}>
          Flight plan confirmed successfully
        </SnackbarAlert>
      ) : (
        ""
      )}
      {approveFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {approveFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {showDisputeSuccessMessage ? (
        <SnackbarAlert type={"success"}>
          Flight plan approved successfully
        </SnackbarAlert>
      ) : (
        ""
      )}{" "}
      {cancelFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {cancelFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {checkDisputeMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {checkDisputeMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {showAlert && (
        <Snackbar
          open={showAlert}
          autoHideDuration={2000}
          onClose={handleAlertClose}
        >
          <Alert
            onClose={handleAlertClose}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      )}
      <ViewHistoricalDataHeader
        airlineData={airlineData}
        flightPlanDetail={flightPlanDetail}
        isEditing={isEditing}
        isAAUser={userData.isAAUser}
        onClickEdit={() => setIsEditing(true)}
        onClickCancel={() => setIsEditing(false)}
        onClickSave={handleSaveFlight}
        onClickConfirm={handleConfirmFlight}
        onClickCancelFlight={() => setShowCancelFlightDialog(true)}
        isLoadingSave={editCommercialFlightPlanMutation?.isPending}
        isLoadingConfirm={
          checkDisputeMutation?.isPending ||
          approveFlightPlanMutation?.isPending
        }
        onClickPrint={handlePrint}
        flightPlanApprovals={flightPlanApprovals?.data}
        isLoadingFlightPlanApprovals={
          isLoadingFlightPlanApprovals || isFetchingFlightPlanApprovals
        }
      />
      {userData?.isAAUser &&
        (flightPlanDetail?.adsbVerified || flightPlanDetail?.bmsVerified) && (
          <div className="view-historical-data-verification-icon-divider-row">
            <VerificationIcon
              adsbVerified={flightPlanDetail?.adsbVerified}
              bmsVerified={flightPlanDetail?.bmsVerified}
            />
          </div>
        )}
      {/* Dispute Status Message */}
      {flightPlanDetail?.disputeStatus === DisputeStatus.RAISED ? (
        <div className="edit-flight-passenger-count-container">
          <div className="dispute-status-text">
            Since you have not accepted the BMS Passenger Count, this flight
            record is sent for dispute processing to Airport Authority.
          </div>
        </div>
      ) : null}
      <ViewHistoricalDataTable
        isEditing={isEditing}
        airportData={airportData}
        flightPlanDetail={flightPlanDetail}
        editedFlightDetails={editedFlightDetails}
        setEditedFlightDetails={setEditedFlightDetails}
      />
      {(userData.isAAUser ||
        flightPlanDetail?.disputeStatus ===
        DisputeStatus.AA_FORCED_BMS_COUNT) && (
          <PassengerCountDisplay flightPlanDetail={flightPlanDetail} />
        )}
      <div className="view-history-passenger-count-display-container">
        <div className="view-history-passenger-count-table">
          <div className="view-history-passenger-count-table-header">
            <div className="view-history-passenger-count-table-cell">
              Airline Total Boarded Pax
            </div>
            <div className="view-history-passenger-count-table-cell">
              Airline Exempted Pax
            </div>
            {infantPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                Infant
              </div>
            )}
            {militaryPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                Military
              </div>
            )}
            {governmentPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                Government
              </div>
            )}
            {diplomatsPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                Diplomat
              </div>
            )}
            <div className="view-history-passenger-count-table-cell">
              Crew Members
            </div>
          </div>
          <div className="view-history-passenger-count-table-row">
            <div className="view-history-passenger-count-table-cell">
              <p className={getAirlineCountClassName()}>
                {flightPlanDetail?.airlinePassengerCount}
              </p>
            </div>
            <div className="view-history-passenger-count-table-cell">
              <p className={getAirlineCountClassName()}>
                {flightPlanDetail?.airlineExemptedPassengerCount}
              </p>
            </div>
            {infantPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                <p className={getAirlineCountClassName()}>
                  {flightPlanDetail?.airlineInfant}
                </p>
              </div>
            )}
            {militaryPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                <p className={getAirlineCountClassName()}>
                  {flightPlanDetail?.airlineMilitary}
                </p>
              </div>
            )}
            {governmentPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                <p className={getAirlineCountClassName()}>
                  {flightPlanDetail?.airlineGovernment}
                </p>
              </div>
            )}

            {diplomatsPassengerEnabled && (
              <div className="view-history-passenger-count-table-cell">
                <p className={getAirlineCountClassName()}>
                  {flightPlanDetail?.airlineDiplomats}
                </p>
              </div>
            )}
            <div className="view-history-passenger-count-table-cell">
              {flightPlanDetail?.deadheadCrews &&
                flightPlanDetail?.deadheadCrews?.length > 0 ? (
                <div className="view-history-crew-members-list">
                  {flightPlanDetail?.deadheadCrews?.map((crew, index) => (
                    <div key={index} className="view-history-crew-member-item">
                      <div className="view-history-crew-member-name">
                        {crew.passengerName} - {crew.seatNumber}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={getAirlineCountClassName()}>0</p>
              )}
            </div>
          </div>
        </div>
        <div className="view-history-passenger-count-divider"></div>
        {!userData.isAAUser &&
          !isDepartureAirportInternational &&
          flightPlanDetail?.isAutoCompleted !== undefined &&
          flightPlanDetail?.isAutoCompleted === false &&
          ((flightPlanDetail?.disputeStatus === DisputeStatus.NOT_RAISED &&
            flightPlanDetail?.status !== FLIGHT_PLAN_STATUS.AA_APPROVAL &&
            flightPlanDetail?.status !== FLIGHT_PLAN_STATUS.AIRLINE_APPROVAL) ||
            flightPlanDetail?.disputeStatus === undefined) && (
            <button
              className="passenger-count-update-button"
              onClick={() => setShowAddPassengerCountDialog(true)}
            >
              Update
            </button>
          )}
      </div>
      {(flightPlanApprovals?.data?.length >= 1 ||
        flightPlanDetail?.isAutoCompleted) && (
          <ViewHistoricalDataApprovedView
            flightApprovalData={flightPlanApprovals?.data}
            disputeStatus={flightPlanDetail?.disputeStatus}
            flightPlanDetail={flightPlanDetail}
          />
        )}
      <div className="edit-flight-details-container">
        {flightPlanDetail?.status !== FLIGHT_PLAN_STATUS.CANCELLED ? (
          <ViewHistoricalDataPaymentSummary
            flightPlanDetail={flightPlanDetail}
          />
        ) : (
          <div />
        )}
      </div>
      <AddPassengerCountDialog
        isOpen={showAddPassengerCountDialog}
        onClose={() => setShowAddPassengerCountDialog(false)}
        onSave={handleAddPassengerCountSave}
        initialData={flightPlanDetail}
        isLoading={updatePassengerCountMutation?.isPending}
      />
      <DisputeReasonDialog
        isOpen={showDisputeReasonDialog}
        onClose={() => setShowDisputeReasonDialog(false)}
        onSave={handleDisputeReasonSave}
        isLoading={checkDisputeMutation?.isPending}
      />
      <PassengerCountMismatchDialog
        isOpen={showPassengerCountMismatchDialog}
        onClose={() => setShowPassengerCountMismatchDialog(false)}
        onRejectBMSCount={handleRejectBMSCount}
        onUpdateAirlineCount={handleUpdateAirlineCount}
        passengerData={getPassengerMismatchData()}
      />
      <CancelFlightDialog
        open={showCancelFlightDialog}
        onClose={() => setShowCancelFlightDialog(false)}
        onClickYes={handleCancelFlight}
        isLoading={cancelFlightPlanMutation?.isPending}
      />
    </div>
  );
};

export default ViewHistoricalData;
